import { Exchange } from '../../../types/exchange';

export interface OnboardingStepMeta {
  n: string;
  key: string;
  title: string; // big Bebas title shown in the rail
  short: string;
}

export const ONBOARDING_STEPS: OnboardingStepMeta[] = [
  { n: '01', key: 'account', title: 'User Onboarding', short: 'Account' },
  { n: '02', key: 'exchange', title: 'Exchange Connection', short: 'Exchange' },
  { n: '03', key: 'info', title: 'Useful Information', short: 'Information' },
  { n: '04', key: 'pages', title: 'Pages Overview', short: 'Pages' },
  { n: '05', key: 'done', title: 'Onboarding Completed', short: 'Complete' },
];

export interface ExchangeMeta {
  id: Exchange;
  name: string; // display name, e.g. 'BINANCE'
  monogram: string;
  needsPassphrase: boolean;
  // Each row is a token list; odd-indexed tokens are emphasized in the accent color.
  steps: string[][];
}

// Exchanges usable for candle-fetching / CPC behind the scenes but NOT selectable for a live
// trading connection yet. Bybit: EU accounts can't link a personal trading bot, so it is hidden
// from the connection UI (onboarding + account) while still powering history/CPC server-side.
// Remove from this list to re-enable the connection button.
export const HIDDEN_CONNECTION_EXCHANGES: Exchange[] = [Exchange.Bybit];

export const EXCHANGES: ExchangeMeta[] = [
  {
    id: Exchange.Binance,
    name: 'BINANCE',
    monogram: 'B',
    needsPassphrase: false,
    steps: [
      ['Log into', 'Binance'],
      ['Navigate to', 'Account', 'from the top right menu'],
      ['In the left menu, select', 'API Management'],
      ['Click', 'Create API', 'on the right'],
      ['Name the key', 'Automagically'],
      ['Click', 'Edit Restrictions'],
      ['Enable', 'Reading', 'and', 'Spot & Margin Trading', '— only these two'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Verify with authenticator app'],
    ],
  },
  {
    id: Exchange.KuCoin,
    name: 'KUCOIN',
    monogram: 'K',
    needsPassphrase: true,
    steps: [
      ['Log into', 'KuCoin'],
      ['Open', 'API Management', 'under Account Security'],
      ['Click', 'Create API', '→ select Trade type'],
      ['Set passphrase, name', 'Automagically'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Confirm scope:', 'General', 'and', 'Spot Trade', '— never Withdraw'],
      [
        'Optional — also tick',
        'Earn',
        'to let idle balances earn interest. With it on, balances are moved in and out of KuCoin savings automatically and redeemed the moment an order needs them. Leave it off and nothing changes.',
      ],
      ['Verify with email + Google authenticator'],
    ],
  },
  {
    id: Exchange.MexC,
    name: 'MEXC',
    monogram: 'M',
    needsPassphrase: false,
    steps: [
      ['Log into', 'MEXC'],
      ['Open the user menu →', 'API Management'],
      ['Click', 'Create New API', '— Memo:', 'Automagically'],
      ['Confirm via email and authenticator'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Tick', 'SPOT_ACCOUNT_READ', 'and', 'SPOT_DEAL_MASTER'],
      ['Leave', 'WITHDRAW', 'and', 'FUTURES', 'unchecked'],
      ['Confirm via email and authenticator'],
    ],
  },
  {
    id: Exchange.Coinbase,
    name: 'COINBASE',
    monogram: 'C',
    needsPassphrase: false,
    steps: [
      ['Log into', 'Coinbase Advanced Trade'],
      ['Open', 'Settings → API'],
      ['Click', 'New API Key', '— Nickname:', 'Automagically'],
      ['Select the portfolio the bot will trade on'],
      ['Permissions:', 'View', '+', 'Trade'],
      ['Do not enable', 'Transfer', '— ever'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Verify with 2FA'],
    ],
  },
  {
    id: Exchange.Kraken,
    name: 'KRAKEN',
    monogram: 'KR',
    needsPassphrase: false,
    steps: [
      ['Log into', 'Kraken'],
      ['Open', 'Settings → Connections & API'],
      ['Click', 'Create API key', '— Name:', 'Automagically'],
      [
        'Permissions:',
        'Funds permissions - Query',
        '+',
        'All Orders and trades permissions',
      ],
      ['Do not enable', 'Withdraw Funds', '— ever'],
      ['Enable', 'WebSocket interface'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Verify with 2FA'],
    ],
  },
  {
    id: Exchange.Phemex,
    name: 'PHEMEX',
    monogram: 'P',
    needsPassphrase: false,
    steps: [
      ['Log into', 'Phemex'],
      ['Open', 'Settings → API Management'],
      ['Click', 'Create New API key', '— Name:', 'Automagically'],
      ['Permissions:', 'Read & Trade', '— never Withdraw'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Verify with 2FA'],
    ],
  },
  {
    id: Exchange.Bybit,
    name: 'BYBIT',
    monogram: 'BY',
    needsPassphrase: false,
    steps: [
      ['Log into', 'Bybit'],
      ['Open', 'Account → API Management'],
      ['Click', 'Create New Key', '— System-generated, Name:', 'Automagically'],
      ['Permissions:', 'Read-Write', '— Spot Trade only, never Withdraw'],
      ['Enter the following IP for whitelisting:', '161.97.64.122'],
      ['Verify with 2FA'],
    ],
  },
];

export interface InfoCard {
  n: string;
  k: string;
  body: string;
}

export const INFO_CARDS: InfoCard[] = [
  {
    n: '01',
    k: 'Credits system',
    body: 'Initially you receive 50 credits. Subscribing to a bot costs 1 credit; creating a new bot from scratch costs 2. When multiple users subscribe to the same bot, you get half the credit back. Max active subscriptions: 25–50 depending on bot popularity.',
  },
  {
    n: '02',
    k: 'Exchange connections',
    body: 'For bots to operate, they require limited access to your exchange account — read balance and perform buy/sell. Bots are never able to withdraw your balance. The scope is enforced on the exchange itself, not just by us.',
  },
  {
    n: '03',
    k: 'Pausing subscriptions',
    body: 'At any time, you can pause a subscription. Transactions stop. Balance is untouched. Be aware: if you pause while the bot has bought, you remain exposed to market movement — if the market drops, your balance does too.',
  },
];

export const PAGES_CARDS: InfoCard[] = [
  {
    n: 'P01',
    k: 'Overview page',
    body: 'A quick overview of your subscriptions — which are active, balances and an insightful chart of how your portfolio is doing. From here you click into any subscription for detail.',
  },
  {
    n: 'P02',
    k: 'Discover page',
    body: 'Every bot available on connected exchanges. Pick a coin and choose a starting balance. Balance can be tuned anytime; bots not on a connected exchange are hidden.',
  },
  {
    n: 'P03',
    k: 'Subscription page',
    body: 'Full insight into a bot: balance, every transaction, deep TA chart. You can adjust balance, pause / resume or edit the exchange connection right here.',
  },
];

export const HINTS: string[] = [
  'Fill all four fields to advance',
  'Connection pending — advance disabled until tested',
  'Email preference saved with the account',
  "Take a moment — these three pages are where you'll live",
  'Last step — review the summary then enter the app',
];
